'use client';

import React, { useEffect, useRef, useState } from 'react';
import Icon from '@/components/ui/AppIcon';
import { profile } from '@/config';
import { getBrandIcon } from '@/lib/socialBrandIcons';

interface SocialLink {
  id: string;
  originalId: string;
  name: string;
  icon: string;
  href: string;
  label: string;
}

const AboutContact: React.FC = () => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLElement>(null);

  const socialLinks: SocialLink[] = profile.socials.map((social) => ({
    id: `about_${social.id}`,
    originalId: social.id,
    name: social.label,
    icon: social.icon,
    href: social.url,
    label: `${social.label} Profile`,
  }));

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible(true);
          }
        });
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);

  return (
    <section data-component-id="src/app/about/components/AboutContact.tsx:54:4" data-component-path="src/app/about/components/AboutContact.tsx" data-component-line="54" data-component-end-line="126" data-component-file="AboutContact.tsx" data-component-name="section" data-component-content="%7B%22elementName%22%3A%22section%22%2C%22className%22%3A%22py-24%20border-t%20border-border%22%7D"
      ref={sectionRef}
      className="py-24 border-t border-border"
    >
      <div data-component-id="src/app/about/components/AboutContact.tsx:58:6" data-component-path="src/app/about/components/AboutContact.tsx" data-component-line="58" data-component-end-line="125" data-component-file="AboutContact.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22max-w-4xl%20mx-auto%20text-center%22%7D" className="max-w-4xl mx-auto text-center">
        {/* Headline */}
        <h2 data-component-id="src/app/about/components/AboutContact.tsx:60:8" data-component-path="src/app/about/components/AboutContact.tsx" data-component-line="60" data-component-end-line="66" data-component-file="AboutContact.tsx" data-component-name="h2" data-component-content="%7B%22elementName%22%3A%22h2%22%2C%22textContent%22%3A%22Let's%20Connect%22%7D"
          className={`text-4xl md:text-6xl font-bold tracking-tight mb-6 transition-all duration-1000 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          Let's Connect
        </h2>

        {/* Subtext */}
        <p data-component-id="src/app/about/components/AboutContact.tsx:69:8" data-component-path="src/app/about/components/AboutContact.tsx" data-component-line="69" data-component-end-line="77" data-component-file="AboutContact.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22textContent%22%3A%22I'm%20always%20open%20to%20discussing%20new%20projects%2C%20creative%20ideas%2C%20or%5Cn%20%20%20%20%20%20%20%20%20%20opportunities%20to%20be%20part%20of%20your%20vision.%22%7D"
          className={`text-lg md:text-xl text-muted-foreground font-light mb-12 transition-all duration-1000 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
          style={{ transitionDelay: '0.2s' }}
        >
          I'm always open to discussing new projects, creative ideas, or
          opportunities to be part of your vision.
        </p>

        {/* Primary CTA */}
        <div data-component-id="src/app/about/components/AboutContact.tsx:80:8" data-component-path="src/app/about/components/AboutContact.tsx" data-component-line="80" data-component-end-line="93" data-component-file="AboutContact.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D"
          className={`mb-12 transition-[opacity,transform] duration-1000 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
          style={{ transitionDelay: '0.3s' }}
        >
          <a data-component-id="src/app/about/components/AboutContact.tsx:86:10" data-component-path="src/app/about/components/AboutContact.tsx" data-component-line="86" data-component-end-line="92" data-component-file="AboutContact.tsx" data-component-name="a" data-component-content="%7B%22elementName%22%3A%22a%22%2C%22className%22%3A%22inline-flex%20items-center%20gap-3%20px-8%20py-4%20bg-primary%20hover%3Abg-primary%2F90%20text-white%20rounded-full%20font-semibold%20text-lg%20transition-all%20duration-300%20hover%3Ascale-105%20glow-effect%22%7D"
            href={`mailto:${profile.email}`}
            className="inline-flex items-center gap-3 px-8 py-4 bg-primary hover:bg-primary/90 text-white rounded-full font-semibold text-lg transition-all duration-300 hover:scale-105 glow-effect"
          >
            <Icon data-component-id="src/app/about/components/AboutContact.tsx:90:12" data-component-path="src/app/about/components/AboutContact.tsx" data-component-line="90" data-component-end-line="90" data-component-file="AboutContact.tsx" data-component-name="Icon" data-component-content="%7B%22elementName%22%3A%22Icon%22%2C%22name%22%3A%22EnvelopeIcon%22%7D" name="EnvelopeIcon" size={24} variant="outline" />
            <span data-component-id="src/app/about/components/AboutContact.tsx:91:12" data-component-path="src/app/about/components/AboutContact.tsx" data-component-line="91" data-component-end-line="91" data-component-file="AboutContact.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22textContent%22%3A%22Send%20Message%22%7D">Send Message</span>
          </a>
        </div>

        {/* Social Links */}
        <div data-component-id="src/app/about/components/AboutContact.tsx:96:8" data-component-path="src/app/about/components/AboutContact.tsx" data-component-line="96" data-component-end-line="124" data-component-file="AboutContact.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D"
          className={`flex flex-wrap items-center justify-center gap-2 sm:gap-4 md:gap-6 transition-[opacity,transform] duration-1000 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
          style={{ transitionDelay: '0.4s' }}
        >
          {socialLinks.map((social) => {
            const BrandIcon = getBrandIcon(social.originalId);
            return (
              <a data-component-id="src/app/about/components/AboutContact.tsx:105:14" data-component-path="src/app/about/components/AboutContact.tsx" data-component-line="105" data-component-end-line="121" data-component-file="AboutContact.tsx" data-component-name="a" data-component-content="%7B%22elementName%22%3A%22a%22%2C%22className%22%3A%22flex%20items-center%20gap-2%20px-3%20py-2%20md%3Apx-4%20glass-card%20rounded-full%20hover%3Ascale-105%20hover%3Aglow-effect%20transition-transform%20duration-300%22%7D"
                key={social.id}
                href={social.href}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 px-3 py-2 md:px-4 glass-card rounded-full hover:scale-105 hover:glow-effect transition-transform duration-300"
                aria-label={social.label}
              >
                {BrandIcon ? (
                  <BrandIcon data-component-id="src/app/about/components/AboutContact.tsx:114:18" data-component-path="src/app/about/components/AboutContact.tsx" data-component-line="114" data-component-end-line="114" data-component-file="AboutContact.tsx" data-component-name="BrandIcon" data-component-content="%7B%22elementName%22%3A%22BrandIcon%22%2C%22className%22%3A%22w-5%20h-5%22%7D" className="w-5 h-5" />
                ) : (
                  <Icon data-component-id="src/app/about/components/AboutContact.tsx:116:18" data-component-path="src/app/about/components/AboutContact.tsx" data-component-line="116" data-component-end-line="116" data-component-file="AboutContact.tsx" data-component-name="Icon" data-component-content="%7B%22elementName%22%3A%22Icon%22%7D" name={social.icon} size={20} variant="outline" />
                )}
                <span data-component-id="src/app/about/components/AboutContact.tsx:118:16" data-component-path="src/app/about/components/AboutContact.tsx" data-component-line="118" data-component-end-line="120" data-component-file="AboutContact.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22text-sm%20font-medium%20hidden%20md%3Ainline%22%7D" className="text-sm font-medium hidden md:inline">
                  {social.name}
                </span>
              </a>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default AboutContact;